import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { GenericLoading } from 'components/ui/Loader';
import { BorrowerTabs } from './components/sections/BorrowerTabs';
import { BorrowerQuoteHeader } from './components/borrowers/BorrowerQuoteHeader';
import { BorrowerContractInformations } from './components/borrowers/BorrowerContractInformations';
import BorrowerProcessTracker from './components/borrowers/BorrowerProcessTracker';
import BorrowerDocuments from './components/borrowers/BorrowerDocuments';
import { AbandonQuoteButton } from './components/AbandonQuoteButton';
import { CancelledQuoteBanner } from './components/CancelledQuoteBanner';
import {
  useBorrowerGlobalInfos,
  useIsBorrowerQuoteCancelled,
  useCanAbandonBorrowerQuote
} from './store';
import { ContractualizationLayout } from './components/layout';
import { useSubscriptionProcess } from '../contexts/SubscriptionProcessContext';
import { BorrowerProjectDocumentsSection } from '../components/BorrowerProjectDocumentsSection';

/**
 * Composant principal pour le flux de contractualisation emprunteur
 *
 * Data flow:
 * - Les données viennent du borrowerContractualizationStore (single source of truth)
 * - Le store est peuplé par fetchSubscriptionProcess dans SubscriptionProcessWrapper
 * - On utilise refreshSubscriptionProcess pour rafraîchir les données
 * - La synchronisation SignalR est gérée par useBorrowerDocumentsSync dans BorrowerDocuments
 */
export const BorrowerContractualizationStep = () => {
  const { borrowerId, borrowerProjectId } = useParams<{
    borrowerId: string;
    borrowerProjectId: string;
    quoteId: string;
  }>();

  // Données du store centralisé (peuplé par fetchSubscriptionProcess)
  const globalInfos = useBorrowerGlobalInfos();
  const isCancelled = useIsBorrowerQuoteCancelled();
  const canAbandon = useCanAbandonBorrowerQuote();
  const { state: subscriptionState, refreshSubscriptionProcess } =
    useSubscriptionProcess();

  const refresh = useCallback(() => {
    refreshSubscriptionProcess();
  }, [refreshSubscriptionProcess]);

  // Show loading while subscription process is loading or data not available
  if (subscriptionState.isLoading || !globalInfos) {
    return <GenericLoading loading isFullPage />;
  }

  const tabsComponent =
    globalInfos && globalInfos.quoteTabs.length > 1 ? (
      <BorrowerTabs
        tabs={globalInfos.quoteTabs || []}
        borrowerProjectId={borrowerProjectId}
        activeTab={borrowerId}
      />
    ) : undefined;

  const headerComponent = (
    <BorrowerQuoteHeader globalInfos={globalInfos} borrowerId={borrowerId} />
  );

  return (
    <ContractualizationLayout tabs={tabsComponent} header={headerComponent}>
      {isCancelled && <CancelledQuoteBanner />}
      {/*
        Devis abandonné : on fige toutes les interactions du contenu (cartes
        d'information, suivi du dossier, documents). Le header et les tabs
        restent cliquables pour que l'utilisateur puisse naviguer vers un
        autre emprunteur du projet ou quitter l'écran.
        - pointer-events-none : neutralise clics/scrolls custom
        - opacity-60 : signal visuel "désactivé"
        - select-none : pas de sélection texte (évite l'illusion d'action)
        - aria-disabled : a11y / lecteurs d'écran
      */}
      <div
        aria-disabled={isCancelled || undefined}
        className={`flex flex-col gap-6${
          isCancelled ? ' pointer-events-none select-none opacity-60' : ''
        }`}
      >
        <BorrowerContractInformations
          borrowerId={borrowerId}
          borrowerProjectId={borrowerProjectId}
        />
        <BorrowerProcessTracker refresh={refresh} borrowerId={borrowerId} />
        <BorrowerDocuments key={globalInfos.quoteId} />
        {/* Documents du projet (offre de prêt, tableau d'amortissement) : consultation
            et remplacement (versionné) pendant la contractualisation. */}
        {borrowerProjectId && (
          <BorrowerProjectDocumentsSection borrowerProjectId={borrowerProjectId} />
        )}
      </div>

      {/* Action discrète d'abandon du devis, en bas, hors zone figée. */}
      <AbandonQuoteButton devisId={globalInfos.quoteId} canAbandon={canAbandon} />
    </ContractualizationLayout>
  );
};
